import { z } from 'zod';
import { adresseSchema, idSchema } from './model/commun.js';
import { DEVISES, MODES_REGLEMENT } from './model/index.js';
const sourceJuridique = 'Code pénal, art. 321-7 et R. 321-1 et suivants ; textes d’application à confirmer.';
function regle(cle, valeur, note) {
    return { cle, valeur, source: sourceJuridique, statut: 'hypothese', note };
}
export const REGLES_REGISTRE_POLICE = {
    mentionsCommunes: regle('mentionsCommunes', [
        'modeAcquisition',
        'dateAcquisition',
        'objet.nature',
        'objet.numeroInventaire',
        'devise',
        'vendeur.qualite',
        'vendeur.adresse.ligne1',
        'vendeur.adresse.codePostal',
        'vendeur.adresse.ville',
        'vendeur.adresse.pays',
    ], 'La nature décrit aussi les objets hors catalogue ; artiste, titre, technique, dimensions, signes et photo sont complémentaires.'),
    mentionsParticulier: regle('mentionsParticulier', [
        'vendeur.nom',
        'vendeur.prenom',
        'piece.nature',
        'piece.numero',
        'piece.delivreeLe',
        'piece.autorite',
    ], 'Identité et mentions de la pièce à faire valider.'),
    mentionsProfessionnel: regle('mentionsProfessionnel', ['vendeur.raisonSociale', 'vendeur.siren'], 'Identification du professionnel à faire valider.'),
    mentionsAcquisition: regle('mentionsAcquisition', ['prixAchatCentimes', 'modeReglement'], 'Prix entier en centimes et règlement pour achat ou échange ; prix nul pour un dépôt.'),
    conservationAnnees: regle('conservationAnnees', 10, 'Après la dernière mention du groupe, à confirmer avant publication.'),
    conservationPropositionsEnAttenteJours: regle('conservationPropositionsEnAttenteJours', 365, 'Proposition jamais inscrite ni écartée, depuis sa dernière modification, à confirmer.'),
    conservationScansOrphelinsJours: regle('conservationScansOrphelinsJours', 30, 'Scan de pièce jamais rattaché à une proposition ni à une inscription, à confirmer.'),
    inscrireDepots: regle('inscrireDepots', true, 'Inscription des dépôts reçus à confirmer.'),
    modesAcquisition: regle('modesAcquisition', ['achat', 'echange', 'depot'], 'Modes inscrits à confirmer.'),
    modesProvenanceInscrits: regle('modesProvenanceInscrits', ['achat'], 'Correspondance avec les modes de provenance existants.'),
    naturesSortie: regle('naturesSortie', ['vente', 'restitution', 'destruction'], 'Natures de sortie à confirmer.'),
};
const texte = z.string().trim().max(2000);
const date = z
    .union([z.date(), z.iso.date(), z.iso.datetime({ offset: true })])
    .transform((valeur) => new Date(valeur));
const objetSchema = z
    .object({
    oeuvreId: idSchema.optional(),
    exemplaireId: idSchema.optional(),
    nature: texte,
    artiste: texte.optional(),
    titre: texte.optional(),
    technique: texte.optional(),
    dimensions: texte.optional(),
    signesDistinctifs: texte.optional(),
    photoMediaId: idSchema.optional(),
    numeroInventaire: texte.optional(),
})
    .strict();
const vendeurSchema = z
    .object({
    contactId: idSchema.nullable().optional(),
    qualite: z.enum(['particulier', 'professionnel']),
    nom: texte.optional(),
    prenom: texte.optional(),
    raisonSociale: texte.optional(),
    adresse: adresseSchema,
    siren: z
        .string()
        .trim()
        .regex(/^\d{9}$/)
        .optional(),
})
    .strict();
const pieceSchema = z
    .object({
    nature: texte,
    numero: texte,
    delivreeLe: date,
    autorite: texte,
    documentId: idSchema.optional(),
})
    .strict();
const formeMentions = {
    modeAcquisition: z.enum(REGLES_REGISTRE_POLICE.modesAcquisition.valeur),
    dateAcquisition: date,
    objet: objetSchema,
    prixAchatCentimes: z.number().int().nonnegative().nullable().optional(),
    devise: z.enum(DEVISES),
    modeReglement: z.enum(MODES_REGLEMENT).optional(),
    vendeur: vendeurSchema,
    piece: pieceSchema.optional(),
};
export const brouillonRegistrePoliceSchema = z
    .object({
    ...formeMentions,
    objet: objetSchema.partial().optional(),
    vendeur: vendeurSchema
        .partial()
        .extend({ adresse: adresseSchema.partial().optional() })
        .optional(),
    piece: pieceSchema.partial().optional(),
})
    .partial()
    .strict();
function valeurDe(objet, chemin) {
    return chemin
        .split('.')
        .reduce((valeur, cle) => valeur !== null && typeof valeur === 'object'
        ? valeur[cle]
        : undefined, objet);
}
export function mentionsManquantes(brouillon) {
    const regles = REGLES_REGISTRE_POLICE;
    const chemins = [...regles.mentionsCommunes.valeur];
    if (brouillon.vendeur?.qualite === 'particulier')
        chemins.push(...regles.mentionsParticulier.valeur);
    if (brouillon.vendeur?.qualite === 'professionnel')
        chemins.push(...regles.mentionsProfessionnel.valeur);
    if (brouillon.modeAcquisition && brouillon.modeAcquisition !== 'depot')
        chemins.push(...regles.mentionsAcquisition.valeur);
    return chemins.filter((chemin) => {
        const valeur = valeurDe(brouillon, chemin);
        return (valeur === undefined || valeur === null || (typeof valeur === 'string' && !valeur.trim()));
    });
}
export const mentionsRegistrePoliceSchema = z
    .object(formeMentions)
    .strict()
    .superRefine((mentions, ctx) => {
    for (const chemin of mentionsManquantes(mentions))
        ctx.addIssue({
            code: 'custom',
            path: chemin.split('.'),
            message: 'Mention obligatoire manquante.',
        });
    if (mentions.modeAcquisition === 'depot' && mentions.prixAchatCentimes !== null)
        ctx.addIssue({
            code: 'custom',
            path: ['prixAchatCentimes'],
            message: 'Le prix d’un dépôt doit être nul.',
        });
});
export const sourceRegistrePoliceSchema = z
    .object({
    type: z.enum(['oeuvre', 'provenance', 'mouvement', 'manuelle']),
    id: z.string().trim().min(1).max(100).nullable(),
    propositionId: idSchema.nullable(),
})
    .strict();
export const sortieRegistrePoliceSchema = z
    .object({
    date,
    nature: z.enum(REGLES_REGISTRE_POLICE.naturesSortie.valeur),
    factureRef: texte.optional(),
})
    .strict();
export const rectificationRegistrePoliceSchema = mentionsRegistrePoliceSchema.safeExtend({
    raison: texte.min(1),
});
export const inscriptionRegistrePoliceSchema = mentionsRegistrePoliceSchema
    .safeExtend({
    numero: z.number().int().positive(),
    nature: z.enum(['inscription', 'rectification', 'sortie']),
    cite: z.number().int().positive().nullable(),
    valideLe: date,
    auteur: z.object({ id: z.string().nullable(), nom: texte.min(1) }).strict(),
    source: sourceRegistrePoliceSchema,
    raison: texte.min(1).optional(),
    sortie: sortieRegistrePoliceSchema.optional(),
    empreintePrecedente: z.string().regex(/^[a-f0-9]{64}$/),
    empreinte: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict()
    .superRefine((inscription, ctx) => {
    if (inscription.nature === 'inscription' && inscription.cite !== null)
        ctx.addIssue({
            code: 'custom',
            path: ['cite'],
            message: 'Une inscription initiale ne cite aucun numéro.',
        });
    if (inscription.nature !== 'inscription' && inscription.cite === null)
        ctx.addIssue({
            code: 'custom',
            path: ['cite'],
            message: 'Le numéro de l’inscription initiale est requis.',
        });
    if (inscription.nature === 'rectification' && !inscription.raison)
        ctx.addIssue({ code: 'custom', path: ['raison'], message: 'La raison est obligatoire.' });
    if (inscription.nature === 'sortie' && !inscription.sortie)
        ctx.addIssue({
            code: 'custom',
            path: ['sortie'],
            message: 'Les mentions de sortie sont obligatoires.',
        });
});
export function mentionsDeLInscription(inscription) {
    const { modeAcquisition, dateAcquisition, objet, prixAchatCentimes, devise, modeReglement, vendeur, piece, } = inscription;
    return {
        modeAcquisition,
        dateAcquisition,
        objet,
        prixAchatCentimes,
        devise,
        modeReglement,
        vendeur,
        piece,
    };
}
/** Les métadonnées de persistance n’entrent pas dans l’empreinte métier. */
export function canonique(inscription) {
    const exclus = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'empreinte']);
    function trier(valeur, racine = false) {
        if (valeur instanceof Date)
            return valeur.toISOString();
        if (Array.isArray(valeur))
            return valeur.map((entree) => trier(entree));
        if (valeur !== null && typeof valeur === 'object') {
            return Object.fromEntries(Object.entries(valeur)
                .filter(([cle, contenu]) => contenu !== undefined && (!racine || !exclus.has(cle)))
                .sort(([a], [b]) => (a < b ? -1 : Number(a > b)))
                .map(([cle, contenu]) => [cle, trier(contenu)]));
        }
        return valeur;
    }
    return JSON.stringify(trier(inscription, true));
}
//# sourceMappingURL=registre-police.js.map